/**
 * Auto-heal for stale-deploy chunk failures.
 *
 * Lazy routes load their chunk via dynamic import(). After a deploy, the
 * hashed chunk filenames change and the old ones are removed from S3 — so a
 * tab that booted on the previous index.html will fail with
 * "TypeError: Failed to fetch dynamically imported module" the first time it
 * navigates to a not-yet-loaded lazy route, crashing into the ErrorBoundary.
 *
 * Vite dispatches `vite:preloadError` on window whenever a dynamic import (or
 * one of its preloaded deps) fails to fetch. We intercept it and reload the
 * page once: the reload revalidates index.html (served with
 * Cache-Control: no-cache), picks up the fresh chunk graph, and the
 * navigation succeeds.
 *
 * The sessionStorage guard prevents a reload loop when the failure is NOT
 * stale-deploy (e.g. the user is offline): if we already auto-reloaded within
 * the window, we let the error propagate to the ErrorBoundary instead.
 */
const RELOADED_AT_KEY = "stale-chunk-reloaded-at";
const RELOAD_LOOP_WINDOW_MS = 60_000;

export const initStaleChunkReload = () => {
  window.addEventListener("vite:preloadError", (event) => {
    const lastReloadAt = Number(sessionStorage.getItem(RELOADED_AT_KEY) ?? 0);
    if (Date.now() - lastReloadAt < RELOAD_LOOP_WINDOW_MS) {
      console.warn(
        "[stale-chunk-reload] preloadError again right after an auto-reload — letting it propagate",
      );
      return;
    }
    // preventDefault stops Vite from re-throwing the import error, so the
    // ErrorBoundary never sees it — the reload IS the recovery.
    event.preventDefault();
    sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
    window.location.reload();
  });
};
